import  { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  Divider,
  TextField,
  IconButton,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { sendVerificationCode, verifyAccount, fetchCurrentUser } from '../../redux/usersSlice';
import backgroundImage from '../../assets/icons/twoFactorBackground.png';

const NAVY = '#2F4E8B';
const OUTLINE = '#D4D9E0';

const TwoFactorAuth = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { verificationCodeSent } = useSelector((state) => state.users);

  const handleVerify = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    dispatch(verifyAccount(code))
      .then(() => {
        dispatch(fetchCurrentUser())
          .unwrap()
          .then(() => navigate('/acceuil'));
      })
      .catch(() => setError('Code de vérification invalide ou expiré.'))
      .finally(() => setLoading(false));
  };

  const handleSendCode = () => {
    dispatch(sendVerificationCode())
      .unwrap()
      .then(() => setSuccess('Code de vérification envoyé avec succès.'))
      .catch(() => setError("Échec de l'envoi du code de vérification."));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        px: 2,
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          position: 'relative',
          borderRadius: 4,
          width: '100%',
          maxWidth: 780,
          p: { xs: 5, sm: 6 },
          bgcolor: '#fff',
        }}
      >
        <IconButton
          onClick={() => navigate('/acceuil')}
          sx={{ position: 'absolute', top: 16, left: 16, color: NAVY }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: NAVY, mb: 1 }}>
            Validation en deux étapes
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            Cliquez sur "Envoyer le code de vérification" puis entrez le code reçu ci-dessous.
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            alignItems: 'center',
          }}
        >
          <Box sx={{ width: 360 }}>
            <TextField
              label="Code de Vérification"
              variant="standard"
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value)}
              InputProps={{
                sx: {
                  borderBottom: `1px solid ${OUTLINE}`,
                  '&:after': { borderBottomColor: NAVY },
                },
              }}
            />
          </Box>

          <Button
            variant="contained"
            sx={{
              width: 200,
              height: 40,
              borderRadius: 30,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: NAVY,
              '&:hover': { bgcolor: NAVY },
            }}
            onClick={handleVerify}
            disabled={loading}
          >
            {loading ? 'Vérification...' : 'Vérifier'}
          </Button>

          <Button
            variant="outlined"
            sx={{
              width: 200,
              height: 40,
              borderRadius: 30,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: NAVY,
              color: NAVY,
              '&:hover': { borderColor: NAVY },
            }}
            onClick={handleSendCode}
            disabled={verificationCodeSent}
          >
            {verificationCodeSent ? 'Code Envoyé' : 'Envoyer le Code'}
          </Button>

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
        </Box>

        <Typography
          variant="body2"
          align="center"
          sx={{ color: '#999', mt: 6 }}
        >
          © 2025 Tallentum. Tous droits réservés.
        </Typography>
      </Paper>
    </Box>
  );
};

export default TwoFactorAuth;
